import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Formik, Form } from "formik";
import moment from "moment";
import { toast } from "@/components/toastify";

import useFetchCommonData from "@/hooks/useFetchCommonData";
import {
  useAddAttendanceRegularizationMutation,
  useGetAttendanceRegularizationQuery,
} from "@/api/services/attendanceManagement/monthEndProcess/MonthEndProcess";

import FormikMonthYearPicker from "@/components/forms/FormikMonthYearPicker";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import CommonDataTable from "@/components/dataTableComponents/NewDatatablePagination";
import { StyledButton } from "@/components";

const AdminAttendanceRegularize = () => {
  // Row states
  const [rowActions, setRowActions] = useState({});
  const [rowRemarks, setRowRemarks] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  // Filter and pagination
  const [filter, setFilter] = useState({
    selectEmployee: "",
    selectMonth: "",
    sortDirection: "DESC",
    page: 0,
    pageSize: 10,
    search: "",
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Loading state
  const [loading, setLoading] = useState(false);

  // Fetch common data
  const { data: fetchAllEmployees } = useFetchCommonData("EMPLOYEES");

  // API hooks
  const [updateRegularization] = useAddAttendanceRegularizationMutation();
  const { data: rowDetails = [], refetch, isLoading } =
    useGetAttendanceRegularizationQuery({
      empId: filter.selectEmployee,
      month: filter.selectMonth,
      sortDirection: "DESC",
      page: filter.page,
      size: filter.pageSize,
      search: filter.search
    });

  // Fetch data only when filters/search/page changes (not on mount)
  useEffect(() => {
    // Only refetch if any filter/search/page changes (skip initial automatic fetch)
    if (
      filter.selectEmployee ||
      filter.selectMonth ||
      filter.page > 0 ||
      filter.pageSize !== 10 ||
      filter.search
    ) {
      refetch();
    }
  }, [
    filter.selectEmployee,
    filter.selectMonth,
    filter.page,
    filter.pageSize,
    filter.search,
    refetch
  ]);

  // Only fetch data when filter changes
  const refetchData = useCallback(async () => {
    setLoading(true);
    try {
      await refetch();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [refetch]);

  useEffect(() => {
    refetchData();
  }, [refetchData, filter]);

  // Pagination handlers
  const handleSearchChange = useCallback((value) => {
    setFilter((prev) => ({ ...prev, search: value, page: 0 }));
    setCurrentPage(0);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
    setFilter((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    setFilter((prev) => ({ ...prev, pageSize: newPageSize, page: 0 }));
  }, []);

  // Employee options
  const empOptions = useMemo(
    () =>
      fetchAllEmployees?.map((item) => ({
        value: item.empId,
        label: item.employeeName,
      })) || [],
    [fetchAllEmployees]
  );

  // Map API data to table rows
  const rows = useMemo(() => {
    const raw = rowDetails?.data?.content || [];
    return raw.map((item) => {
      const getMappedTimes = (start, end) =>
        (start || []).map((t, i) => `${t} - ${(end || [])[i]}`).join(", ");

      const reasons =
        item.appliedRegularizations?.map((r) => r.reason).join(", ") || "NA";
      const inOut = getMappedTimes(
        item.appliedRegularizations?.map((r) => r.actualInTime),
        item.appliedRegularizations?.map((r) => r.actualOutTime)
      );
      const dateTime = getMappedTimes(
        item.appliedRegularizations?.map((r) => r.startTime),
        item.appliedRegularizations?.map((r) => r.endTime)
      );

      return {
        id: item.regularizationCode || "NA",
        PendingWith: item.appliedTo || "NA",
        EmployeeNo: item.employeeId || "NA",
        EmpName: item.employeeName || "NA",
        INOUT: inOut || "NA",
        DateTime: dateTime || "NA",
        Duration: item.regularizationDates?.join(" - ") || "NA",
        NoofDays: item.noOfDays || "NA",
        Reasons: reasons,
        AppliedOn: item.appliedDate || "NA",
        regularizationCode: item.regularizationCode || "NA",
      };
    });
  }, [rowDetails]);

  // Validation for selected rows
  const validateRows = useCallback(
    (selectedData) => {
      const errors = {};
      selectedData.forEach(({ id }) => {
        const action = rowActions[id];
        const remark = rowRemarks[id];
        if (action === "reject" && !remark) {
          errors[id] = { remark: "Remark is required when rejecting" };
        }
      });
      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    },
    [rowActions, rowRemarks]
  );

  const removeValidationError = (id, field) => {
    setValidationErrors((prev) => {
      const updated = { ...prev[id] };
      delete updated[field];
      const newErrors = { ...prev };
      if (Object.keys(updated).length > 0) newErrors[id] = updated;
      else delete newErrors[id];
      return newErrors;
    });
  };

  // Handle row selection
  const handleSelectedRowsChange = (newSelectedRows) => {
    const selectedIds = newSelectedRows.map((row) => row.id);
    const newRowActions = {};
    const newRowRemarks = {};
    const newErrors = {};

    newSelectedRows.forEach((row) => {
      const id = row.id;
      const action = rowActions[id];
      const remark = rowRemarks[id];

      if (action === "reject" && !remark) {
        newErrors[id] = { remark: "Remark is required when rejecting" };
      }

      if (action) newRowActions[id] = action;
      if (remark) newRowRemarks[id] = remark;
    });

    setValidationErrors((prev) => {
      const updated = {};
      for (const id of selectedIds) {
        if (newErrors[id]) updated[id] = newErrors[id];
        else if (prev[id]) updated[id] = prev[id];
      }
      return updated;
    });

    setRowActions((prev) => {
      const updated = {};
      for (const id of selectedIds) {
        updated[id] = newRowActions[id] || prev[id] || "approve";
      }
      return updated;
    });

    setRowRemarks((prev) => {
      const updated = {};
      for (const id of selectedIds) {
        updated[id] = newRowRemarks[id] || prev[id] || "";
      }
      return updated;
    });

    setSelectedRows(newSelectedRows);
  };

  // Submit selected rows
  const handleSubmit = async () => {
    if (!validateRows(selectedRows)) return;

    const payload = selectedRows.map(({ id }) => {
      const row = rows.find((r) => r.id === id);
      return {
        regCode: id,
        action: rowActions[id] || "approve",
        remark: rowActions[id] === "reject" ? rowRemarks[id] || "" : "",
        empId: row.EmployeeNo,
        month: moment(row.AppliedOn).format("MM-YYYY"),
      };
    });

    try {
      await updateRegularization({ formData: payload }).unwrap();
      toast("success", "Regularization Updated Successfully!");
      setRowActions({});
      setRowRemarks({});
      setSelectedRows([]);
      setValidationErrors({});
      refetchData();
    } catch (error) {
      toast("error", "Something went wrong!");
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { field: "PendingWith", headerName: "Pending With" },
      { field: "EmployeeNo", headerName: "Employee No" },
      { field: "EmpName", headerName: "Emp Name" },
      { field: "INOUT", headerName: "IN/OUT" },
      { field: "DateTime", headerName: "Date IN/OUT Time" },
      { field: "Duration", headerName: "Duration" },
      { field: "NoofDays", headerName: "No. of Days" },
      { field: "Reasons", headerName: "Reason" },
      { field: "AppliedOn", headerName: "Applied On" },
      {
        field: "Action",
        headerName: "Action",
        flex: 4,
        minWidth: 200,
        renderCell: ({ row }) => {
          const error = validationErrors[row.id]?.action;
          return (
            <FormControl fullWidth sx={{ padding: "8px 0px" }}>
              <Select
                value={rowActions[row.id] || "approve"}
                onChange={(e) => {
                  const value = e.target.value;
                  setRowActions((prev) => ({ ...prev, [row.id]: value }));

                  if (value === "reject" && !(rowRemarks[row.id] || "").trim()) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      [row.id]: { ...(prev[row.id] || {}), remark: "Remark is required when rejecting" },
                    }));
                  } else removeValidationError(row.id, "remark");

                  if (error && value) removeValidationError(row.id, "action");
                }}
              >
                <MenuItem value="approve">Approve</MenuItem>
                <MenuItem value="reject">Reject</MenuItem>
              </Select>
            </FormControl>
          );
        },
      },
      {
        field: "Remarks",
        headerName: "Remarks",
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => {
          const error = validationErrors[row.id]?.remark;
          return (
            <FormControl fullWidth sx={{ padding: "8px 0px" }}>
              <TextField
                value={rowRemarks[row.id] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setRowRemarks((prev) => ({ ...prev, [row.id]: value }));
                  if (error && value) removeValidationError(row.id, "remark");
                }}
                onKeyDown={(e) => e.stopPropagation()}
              />
              {error && <span style={{ color: "red", fontSize: "12px" }}>{error}</span>}
            </FormControl>
          );
        },
      },
    ],
    [rowActions, rowRemarks, validationErrors]
  );

  return (
    <Box>
      <Formik initialValues={{ selectEmployee: "", selectMonth: "" }} onSubmit={() => { }}>
        {({ resetForm, values }) => {
          useEffect(() => {
            const formattedMonth = values.selectMonth
              ? moment(values.selectMonth).format("MM-YYYY")
              : "";
            setFilter((prev) => ({
              ...prev,
              selectEmployee: values.selectEmployee,
              selectMonth: formattedMonth,
              page: 0,
            }));
            setCurrentPage(0);
          }, [values.selectMonth, values.selectEmployee]);

          const handleReset = () => {
            resetForm({ values: { selectEmployee: "", selectMonth: "" } });
            setFilter((prev) => ({
              ...prev,
              selectEmployee: "",
              selectMonth: "",
              page: 0,
              pageSize,
              search: "",
            }));
            setCurrentPage(0);
            setRowActions({});
            setRowRemarks({});
            setValidationErrors({});
            setSelectedRows([]);
            refetchData();
          };

          return (
            <Form>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={4}>
                  <FormikMonthYearPicker
                    name="selectMonth"
                    label="Select Month"
                    required
                    maxDate={new Date()}
                    pickerType="month"
                    placeholder="Select To Month"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormikSelectInput
                    name="selectEmployee"
                    label="Select Employee"
                    required
                    options={empOptions}
                    search
                  />
                </Grid>
                <Grid item xs={12} sm={4} display="flex" alignItems="center" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleReset}
                    sx={{ width: "100px", height: "40px", mr: 2 }}
                  >
                    Reset
                  </Button>
                  {selectedRows.length > 0 && (
                    <StyledButton
                      type="button"
                      onClick={handleSubmit}
                      label="Submit"
                      sx={{ ml: 2 }}
                    />
                  )}
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>

      <CommonDataTable
        columns={columns}
        rows={rows}
        checkbox
        disableRowSelectionOnClick
        selectedRows={selectedRows}
        onSelectedRowsChange={handleSelectedRowsChange}
        search={false}
        loading={loading || isLoading}
        onSearchChange={handleSearchChange}
        pageCount={rowDetails?.data?.totalPages || 0}
        totalRecords={rowDetails?.data?.totalElements || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        currentPage={currentPage}
        currentPageSize={pageSize}
      />
    </Box>
  );
};

export default AdminAttendanceRegularize;
